import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaWhatsapp, FaEnvelope, FaSms } from "react-icons/fa";
import { getReturnLabel, getReturnPath } from "../../../utils/navReturn";
import useAuth from "../../../hooks/useAuth";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import { repoCaseService } from "../../../services/repoCase.service";
import confirmationService from "../../../services/confirmation.service";
import { fieldTrackingService } from "../../../services/fieldTracking.service";
import CaseLocationsMapView from "../../../components/tracking/CaseLocationsMapView";
import {
  buildNotifyBankApiPayload,
  buildNotifyContextFromCase,
  formatNotifyApiResult,
  getBankTracedShareMessage,
  getTraceReportToAdminMessage,
  openBankNotifyShare,
  openWhatsAppUrl,
} from "../../../utils/bankNotifyShare";
import { DEMO_TRACE_NOTE } from "../../../utils/demoTraceData";
import { captureCurrentGps } from "../../../utils/captureCurrentGps";
import AdminExcelFieldGrid from "../../../components/repo/AdminExcelFieldGrid";
import { useEnrichedAdminCase } from "../../../hooks/useEnrichedAdminCase";
import { resolveVehicleDisplayStatus } from "../../../utils/vehicleDisplayStatus";
import "../../../styles/detailsView.css";
import "../../../styles/confirmation.css";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "-";
  }
};

const googleMapsUrl = (lat, lng) =>
  `https://www.google.com/maps?q=${Number(lat)},${Number(lng)}`;

export default function DetailsView() {
  const { auth } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnTo = getReturnPath(searchParams, location.state, "/cases");
  const returnLabel = getReturnLabel(returnTo);

  const caseId = searchParams.get("id");
  const canSendConfirmation = [
    "TEAM_LEADER",
    "HEAD_OFFICE_STAFF",
    "OFFICE_STAFF",
    "REPO_STAFF",
    "REPO_VIEWER",
  ].includes(auth?.user?.role);
  const isRepoAdmin = actsAsAgencyAdmin(auth);

  const [caseData, setCaseData] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [confirmationNote, setConfirmationNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingRemark, setSavingRemark] = useState(false);
  const [sendingConfirmation, setSendingConfirmation] = useState(false);
  const [error, setError] = useState("");

  // Vehicle number lookup: "who traced it" (latest confirmation) per matching case.
  const [vehicleLookup, setVehicleLookup] = useState("");
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [vehicleLookupError, setVehicleLookupError] = useState("");
  const [vehicleLookupResults, setVehicleLookupResults] = useState(null);
  const [notifyStatus, setNotifyStatus] = useState("");
  const [notifySending, setNotifySending] = useState(false);

  const [tracerLocations, setTracerLocations] = useState([]);
  const [tracerLocationsLoading, setTracerLocationsLoading] = useState(false);
  const [tracerLocationsError, setTracerLocationsError] = useState("");

  const { enrichedCase: adminCaseData, enriching: adminCaseEnriching } = useEnrichedAdminCase(
    caseData,
    {
      token: auth?.token,
      caseId,
      enabled: isRepoAdmin && Boolean(caseData) && Boolean(auth?.token),
    }
  );

  const notifyContext = buildNotifyContextFromCase(caseData, auth?.user, auth?.user?.company);
  const notifyPreview = caseData ? getBankTracedShareMessage(caseData, notifyContext) : "";

  const staffTracePreview = useMemo(() => {
    if (!caseData || !canSendConfirmation) return "";
    return getTraceReportToAdminMessage(
      caseData,
      {
        name: auth?.user?.name,
        role: auth?.user?.role,
        phone: auth?.user?.phone,
      },
      { requestNote: confirmationNote }
    );
  }, [caseData, canSendConfirmation, auth?.user, confirmationNote]);

  const tracedLocationHistory = useMemo(() => {
    const fromApi = Array.isArray(tracerLocations) ? tracerLocations : [];
    if (fromApi.length) return fromApi;
    const loc = caseData?.lastKnownLocation;
    const lat = Number(loc?.latitude);
    const lng = Number(loc?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
    return [
      {
        latitude: lat,
        longitude: lng,
        accuracy: loc.accuracy,
        tracerName: loc.tracerName,
        createdAt: loc.updatedAt,
        source: "GPS",
      },
    ];
  }, [tracerLocations, caseData]);

  const latestTracedLocation = tracedLocationHistory[0] || null;

  const handleFillDemoTrace = () => {
    setConfirmationNote(DEMO_TRACE_NOTE);
  };

  const handleVehicleLookup = async (e) => {
    e?.preventDefault?.();
    setVehicleLookupError("");

    const needle = String(vehicleLookup || "").trim();
    if (!needle) {
      setVehicleLookupError("Enter a vehicle number to search.");
      return;
    }

    if (!auth?.token) {
      setVehicleLookupError("You are not logged in.");
      return;
    }

    try {
      setVehicleLookupLoading(true);
      setVehicleLookupResults(null);

      const res = await repoCaseService.searchTracesByVehicleNumber(
        needle,
        auth.token
      );
      setVehicleLookupResults(res?.items || []);
    } catch (err) {
      setVehicleLookupError(
        err?.response?.data?.message || err?.message || "Vehicle search failed."
      );
    } finally {
      setVehicleLookupLoading(false);
    }
  };

  const loadCase = useCallback(async () => {
    if (!auth?.token || !caseId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await repoCaseService.getCaseById(caseId, auth.token);
      setCaseData(res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load case details");
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  }, [auth?.token, caseId]);

  useEffect(() => {
    loadCase();
  }, [loadCase]);

  const loadTracerLocations = useCallback(async () => {
    if (!auth?.token || !caseId) return;
    try {
      setTracerLocationsLoading(true);
      setTracerLocationsError("");
      const res = await fieldTrackingService.getLocations(caseId, auth.token, {
        limit: 50,
      });
      const items = res?.data?.items || [];
      setTracerLocations(items);
    } catch (err) {
      setTracerLocationsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load tracer locations."
      );
      setTracerLocations([]);
    } finally {
      setTracerLocationsLoading(false);
    }
  }, [auth?.token, caseId]);

  useEffect(() => {
    if (!isRepoAdmin) return;
    loadTracerLocations();
  }, [isRepoAdmin, loadTracerLocations]);

  const handleAddRemark = async () => {
    const text = remarkText.trim();

    if (!text) {
      alert("Please enter remark text");
      return;
    }

    try {
      setSavingRemark(true);
      await repoCaseService.addRemark(caseId, text, auth.token);
      setRemarkText("");
      await loadCase();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to add remark");
    } finally {
      setSavingRemark(false);
    }
  };

  const handleSendConfirmation = async () => {
    if (!caseData?._id) return;

    try {
      setSendingConfirmation(true);

      const gps = await captureCurrentGps();
      const res = await confirmationService.create(
        {
          caseId: caseData._id,
          requestNote: confirmationNote,
          ...(gps
            ? {
                latitude: gps.latitude,
                longitude: gps.longitude,
                accuracy: gps.accuracy,
              }
            : {}),
        },
        auth.token
      );
      const traceReport = res?.traceReport;

      setConfirmationNote("");
      await loadCase();

      if (traceReport?.whatsAppUrl) {
        openWhatsAppUrl(traceReport.whatsAppUrl);
        alert(
          "Trace report sent. WhatsApp opened for admin with all Excel / case details auto-filled."
        );
      } else {
        alert("Trace report saved. Admin notified in the app.");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to send confirmation request");
    } finally {
      setSendingConfirmation(false);
    }
  };

  const handleNotifyBankShare = async (channel) => {
    if (!caseData || !caseId || !auth?.token) return;
    setNotifyStatus("");
    setNotifySending(true);

    try {
      await openBankNotifyShare(channel, caseData, {}, notifyContext, {
        token: auth.token,
      });
      if (channel === "whatsapp") {
        setNotifyStatus("WhatsApp opened with auto-filled message (copied to clipboard).");
        return;
      }

      setNotifyStatus(
        `${channel === "email" ? "Email" : "SMS"} opened with auto-filled message.`
      );

      try {
        const res = await repoCaseService.notifyBankTraced(
          caseId,
          buildNotifyBankApiPayload(channel, auth?.user),
          auth.token
        );
        const apiNote = formatNotifyApiResult(res?.data?.results);
        if (apiNote && apiNote !== "Done.") {
          setNotifyStatus(`${channel === "email" ? "Email" : "SMS"} opened. ${apiNote}`);
        }
      } catch {
        /* client share already opened */
      }
    } catch (err) {
      setNotifyStatus(err?.response?.data?.message || "Could not open share.");
    } finally {
      setNotifySending(false);
    }
  };

  if (!isRepoAdmin) {
    return <Navigate to="/find-vehicles" replace />;
  }

  if (!caseId) {
    return <Navigate to="/cases" replace />;
  }

  const backLink = (
    <div className="details-page__top">
      <Link to={returnTo} className="cf-view-back">
        ← Back to {returnLabel}
      </Link>
    </div>
  );

  if (loading) {
    return (
      <div className="details-page">
        {backLink}
        <div className="details-card">
          <h2 className="details-title">Case Details</h2>
          <p>Loading case details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="details-page">
        {backLink}
        <div className="details-card">
          <h2 className="details-title">Case Details</h2>
          <p className="error-text">{error}</p>
          <Link to={returnTo} className="cf-view-back-btn">
            Back to {returnLabel}
          </Link>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="details-page">
        {backLink}
        <div className="details-card">
          <h2 className="details-title">Case Details</h2>
          <p>Case not found.</p>
          <Link to={returnTo} className="cf-view-back-btn">
            Back to {returnLabel}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="details-page">
      {backLink}
      <div className="details-card">
        <div className="details-topbar">
          <h2 className="details-title">Case Details</h2>
        </div>

        <div className="details-grid">
          <div className="details-item">
            <span className="details-label">Customer Name</span>
            <div className="details-value">{caseData.customerName || "-"}</div>
          </div>

          <div className="details-item">
            <span className="details-label">Vehicle Number</span>
            <div className="details-value">{caseData.vehicleNumber || "-"}</div>
          </div>

          {!isRepoAdmin ? (
            <div className="details-item">
              <span className="details-label">Mobile Number</span>
              <div className="details-value">{caseData.mobileNumber || "-"}</div>
            </div>
          ) : null}

          {isRepoAdmin ? (
            <div className="details-item full">
              <span className="details-label">Uploaded Excel sheet (all columns)</span>
              <AdminExcelFieldGrid
                caseData={adminCaseData || caseData}
                enriching={adminCaseEnriching}
                layout="cards"
                className="details-excel-sheet"
              />
            </div>
          ) : null}

          <div className="details-item">
            <span className="details-label">Assigned User</span>
            <div className="details-value">{caseData?.assignedToUserId?.name || "-"}</div>
          </div>

          <div className="details-item full">
            <span className="details-label">Latest traced by</span>
            <div className="details-value">
              {caseData?.latestTraceReport?.requestedByName ? (
                <>
                  {caseData.latestTraceReport.requestedByName} ·{" "}
                  {caseData.latestTraceReport.requestedByRoleLabel ||
                    caseData.latestTraceReport.requestedByRole ||
                    "—"}
                  {caseData.latestTraceReport.requestedByPhone ? (
                    <> · {caseData.latestTraceReport.requestedByPhone}</>
                  ) : null}
                  {caseData.latestTraceReport.reportedAt ? (
                    <>
                      {" "}
                      · {new Date(caseData.latestTraceReport.reportedAt).toLocaleString()}
                    </>
                  ) : null}
                </>
              ) : (
                "-"
              )}
            </div>
          </div>

          <div className="details-item">
            <span className="details-label">Field trace status</span>
            <div className="details-value">{caseData.traceStatus || "PENDING"}</div>
          </div>

          <div className="details-item full">
            <span className="details-label">Tracer last known GPS</span>
            <div className="details-value">
              {caseData?.lastKnownLocation?.latitude != null &&
              caseData?.lastKnownLocation?.longitude != null ? (
                <>
                  {caseData?.lastKnownLocation?.tracerName
                    ? `Tracer: ${caseData.lastKnownLocation.tracerName} · `
                    : ""}
                  Lat/Lng:{" "}
                  {Number(caseData.lastKnownLocation.latitude).toFixed(5)},{" "}
                  {Number(caseData.lastKnownLocation.longitude).toFixed(5)}
                  {caseData?.lastKnownLocation?.updatedAt ? (
                    <> · {new Date(caseData.lastKnownLocation.updatedAt).toLocaleString()}</>
                  ) : null}
                  <div style={{ marginTop: 8 }}>
                    <a
                      href={`https://www.google.com/maps?q=${caseData.lastKnownLocation.latitude},${caseData.lastKnownLocation.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#2563eb", fontWeight: 700, textDecoration: "none" }}
                    >
                      Open in Google Maps
                    </a>
                  </div>
                </>
              ) : (
                "-"
              )}
            </div>
          </div>

          <div className="details-item">
            <span className="details-label">Status</span>
            <div className="details-value">{resolveVehicleDisplayStatus(caseData)}</div>
          </div>

          <div className="details-item">
            <span className="details-label">OTP Status</span>
            <div className="details-value">{caseData.otpStatus || "-"}</div>
          </div>

          <div className="details-item">
            <span className="details-label">Blacklist Status</span>
            <div className="details-value">{caseData.blacklistStatus || "-"}</div>
          </div>

          <div className="details-item full">
            <span className="details-label">Address</span>
            <div className="details-value">
              {[
                caseData.addressLine1,
                caseData.addressLine2,
                caseData.city,
                caseData.district,
                caseData.state,
                caseData.pincode,
              ]
                .filter(Boolean)
                .join(", ") || "-"}
            </div>
          </div>

          <div className="details-item full">
            <span className="details-label">Field Notes</span>
            <div className="details-value">{caseData.fieldNotes || "-"}</div>
          </div>

          <div className="details-item">
            <span className="details-label">Last Action</span>
            <div className="details-value">{formatDateTime(caseData.lastActionAt)}</div>
          </div>

          <div className="details-item">
            <span className="details-label">Created At</span>
            <div className="details-value">{formatDateTime(caseData.createdAt)}</div>
          </div>
        </div>
      </div>

      {isRepoAdmin && (
        <>
          <div className="details-card">
            <h3 className="details-section-title">Tracer report</h3>
            <div className="details-grid">
              <div className="details-item">
                <span className="details-label">Tracer name</span>
                <div className="details-value">
                  {caseData?.latestTraceReport?.requestedByName || "-"}
                </div>
              </div>

              <div className="details-item">
                <span className="details-label">Tracer role</span>
                <div className="details-value">
                  {caseData?.latestTraceReport?.requestedByRoleLabel ||
                    caseData?.latestTraceReport?.requestedByRole ||
                    "-"}
                </div>
              </div>

              <div className="details-item">
                <span className="details-label">Reporter mobile</span>
                <div className="details-value">
                  {caseData?.latestTraceReport?.requestedByPhone || "-"}
                </div>
              </div>

              <div className="details-item">
                <span className="details-label">Trace status</span>
                <div className="details-value">{caseData?.latestTraceReport?.status || "-"}</div>
              </div>

              <div className="details-item full">
                <span className="details-label">Tracer report (field note)</span>
                <div className="details-value">
                  {caseData?.latestTraceReport?.requestNote || "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="details-card">
            <h3 className="details-section-title">Vehicle Traced Location</h3>
            {tracerLocationsLoading && !latestTracedLocation ? (
              <p className="muted">Loading traced location…</p>
            ) : tracerLocationsError && !latestTracedLocation ? (
              <p className="error-text">{tracerLocationsError}</p>
            ) : !latestTracedLocation ? (
              <p className="muted">
                No vehicle traced location yet. Location is saved when this vehicle is reported
                (Find Vehicles / WhatsApp) by an agent, staff, team leader, or admin with location on.
              </p>
            ) : (
              <>
                <div className="details-grid">
                  <div className="details-item">
                    <span className="details-label">Vehicle number</span>
                    <div className="details-value">{caseData?.vehicleNumber || "-"}</div>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Customer name</span>
                    <div className="details-value">{caseData?.customerName || "-"}</div>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Traced by</span>
                    <div className="details-value">
                      {latestTracedLocation.tracerName ||
                        caseData?.latestTraceReport?.requestedByName ||
                        "-"}
                    </div>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Agent role</span>
                    <div className="details-value">
                      {caseData?.latestTraceReport?.requestedByRoleLabel ||
                        caseData?.latestTraceReport?.requestedByRole ||
                        "-"}
                    </div>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Agent mobile</span>
                    <div className="details-value">
                      {caseData?.latestTraceReport?.requestedByPhone || "-"}
                    </div>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Latitude</span>
                    <div className="details-value">
                      {Number(latestTracedLocation.latitude).toFixed(6)}
                    </div>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Longitude</span>
                    <div className="details-value">
                      {Number(latestTracedLocation.longitude).toFixed(6)}
                    </div>
                  </div>
                  <div className="details-item">
                    <span className="details-label">Traced date/time</span>
                    <div className="details-value">
                      {formatDateTime(
                        latestTracedLocation.createdAt || latestTracedLocation.updatedAt
                      )}
                    </div>
                  </div>
                </div>
                <p className="details-muted" style={{ margin: "12px 0 8px" }}>
                  Last traced location
                </p>
                <CaseLocationsMapView locations={[latestTracedLocation]} />
                <p style={{ marginTop: 12 }}>
                  <a
                    className="details-btn details-btn-secondary"
                    href={googleMapsUrl(
                      latestTracedLocation.latitude,
                      latestTracedLocation.longitude
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in Google Maps
                  </a>
                </p>
              </>
            )}
          </div>
        </>
      )}

      {canSendConfirmation && (
        <div className="details-card">
          <h3 className="details-section-title">Report traced vehicle to admin</h3>
          <p className="details-muted">
            Team leader / staff submit trace details without photos. Admin receives the full Excel
            case data on WhatsApp automatically. After admin confirms, you upload inventory
            pre/post files (images, videos, PDFs).
          </p>

          <label className="details-notify-preview-wrap">
            <span className="details-label">Preview — WhatsApp message to admin (auto-filled)</span>
            <textarea
              className="details-notify-preview"
              readOnly
              value={staffTracePreview || "Fill field note or use demo data to preview the message."}
              rows={10}
            />
          </label>

          <div className="details-remark-box">
            <textarea
              value={confirmationNote}
              onChange={(e) => setConfirmationNote(e.target.value)}
              rows={4}
              placeholder="Enter field note / live situation note for admin"
            />
          </div>

          <div className="details-trace-actions">
            <button
              type="button"
              className="details-btn details-btn-secondary"
              onClick={handleFillDemoTrace}
              disabled={sendingConfirmation}
            >
              Fill demo data
            </button>
            <button
              className="details-btn"
              onClick={handleSendConfirmation}
              disabled={sendingConfirmation}
            >
              {sendingConfirmation ? "Sending..." : "Send trace to admin (WhatsApp)"}
            </button>
          </div>
        </div>
      )}

      {null}
    </div>
  );
}